<template>
  <div v-if="showEntities" :class="{'d-flex ga-2 justify-center': showAi, 'height-transition-container': true }">
    <div :class="{'show-on-hover-item d-flex align-center ga-1 h-100': true, 'pt-2': showAi}">
      <v-tooltip location="bottom">
        <template v-slot:activator="{ props: props }">
          <v-btn
            v-bind="props"
            class="show-on-hover-item add-data-model-btn"
            variant="plain"
            icon="mdi-plus"
            size="small"
            @click="addDataField(event.id, card.id, card.cardType.triggerType)"
            :disabled="store.state.isWorkflowDisabled"
          />
        </template>
        <span>Add field</span>
      </v-tooltip>
      <v-tooltip v-if="showAi" location="bottom">
        <template v-slot:activator="{ props: props }">
          <v-btn
            v-bind="props"
            class="show-on-hover-item add-data-model-btn"
            variant="plain"
            icon="mdi-creation"
            size="small"
            @click="createTableWithAi()"
            :disabled="store.state.isWorkflowDisabled"
          />
        </template>
        <span>Generate fields with AI</span>
      </v-tooltip>
      <entity-field-selector
        :card="{ ...card, entityId }"
        @onUpdateFields="(fields) => updateDataFields(event.id, card.id, card.cardType.triggerType, fields)"
        :disabled="!entityId"
        :positioningActivator="menuPositioningActivator"
        :location="card.cardType.queryType ? 'right top' : 'right center'"
      >
        <v-tooltip v-if="showFilters" location="bottom" open-delay="100">
          <template v-slot:activator="{ on, props }">
            <v-btn
              class="show-on-hover-item"
              v-bind="props"
              v-on="{ ...on }"
              size="small"
              variant="plain"
              tabindex="-1"
              icon="mdi-list-status"
              :style="{ 'cursor': entityId ? 'pointer' : 'not-allowed' }"
              :disabled="store.state.isWorkflowDisabled"
            />
          </template>
          <span>{{ entityId ? 'Select fields' : 'Disabled because no entity is selected' }}</span>
        </v-tooltip>
      </entity-field-selector>
      <v-tooltip location="bottom" open-delay="100">
        <template v-slot:activator="{ on, props }">
          <v-btn
            class="show-on-hover-item"
            v-bind="props"
            v-on="{ ...on }"
            size="small"
            variant="plain"
            @click="removeCardAndModel(event, card)"
            tabindex="-1"
            icon="mdi-trash-can-outline"
            :loading="loadingDeleteModel"
            :disabled="store.state.isWorkflowDisabled"
          />
        </template>
        <span>Delete card</span>
      </v-tooltip>
    </div>
  </div>
</template>

<script setup>
import { useStore } from 'vuex'
import { computed, getCurrentInstance } from 'vue'
import EntityFieldSelector from '@/components/EntityFieldSelector'
import useDomainModel from '@/composables/useDomainModel'
import { cardHelpers } from '@/mixins/cardHelpers'
import usePrompts from '@/composables/usePrompts'
import workflowApi from '@/api/workflowApi'

const {
  event,
  card,
  showAi,
  showFilters,
  menuPositioningActivator
} = defineProps({
  event: Object,
  card: Object,
  showAi: Boolean,
  showFilters: Boolean,
  menuPositioningActivator: Object
})

const store = useStore()
const { proxy } = getCurrentInstance()

const workflow = computed(() => store.state.workflow.workflow)
const showEntities = computed(() => store.state.domainModelShowEntities)

const entityId = computed(() =>
  card.cardType.triggerType ? cardHelpers.methods.getEntityCard(event)?.entityId : card.entityId
)

const { addDataField, updateDataFields, removeCardAndModel, loadingDeleteModel } = useDomainModel()
const { getReadModelFromWorkflow, getReadModelFromEntity, getWriteModel } = usePrompts()

const createTableWithAi = async () => {
  proxy.$loadingState.start()

  let newDataModel
  const lane = workflow.value.lanes.find((item) => item.id === event.laneId)

  if (card.cardType.queryType) {
    const selectedEntity = (workflow.value.dataModels ?? []).find((entity) => entity.id === card.entityId)
    const response = selectedEntity
      ? await getReadModelFromEntity(event.id, selectedEntity)
      : await getReadModelFromWorkflow(event.id)

    if (!response?.readModel?.length) {
      store.commit('snackbar/showMessage', {
        content: 'Error adding',
        timeout: 6000,
        color: 'red',
        location: 'center center'
      }, { root: true })
      proxy.$loadingState.stop()
      return
    }

    newDataModel = {
      name: `${event.description} by ${lane.name}`,
      type: 'Read Model',
      dataFields: response.readModel.map((item) => ({ name: item.fieldName })),
      cardId: card.id
    }
  }

  if (card.cardType.triggerType) {
    const response = await getWriteModel(event.id)

    if (!response?.writeModel?.length) {
      store.commit('snackbar/showMessage', {
        content: 'Error adding',
        timeout: 6000,
        color: 'red',
        location: 'center center'
      }, { root: true })
      proxy.$loadingState.stop()
      return
    }

    newDataModel = {
      name: `${event.description} by ${lane.name}`,
      type: 'Event Story',
      dataFields: response.writeModel.map((item) => ({ name: item.fieldName })),
      cardId: card.id
    }
  }

  await workflowApi.updateDataModels({
    eventId: event.id,
    dataModelsOnEvent: [...(event.dataModels ?? []), newDataModel],
    workflow: workflow.value
  })

  store.commit('snackbar/showMessage', {
    content: 'Completed',
    timeout: 3000,
    color: 'blue'
  }, { root: true })
  proxy.$loadingState.stop()
}
</script>
